export interface ScannedProduct {
  title: string
  editionLabel?: string
  priceNumber: number
  priceText: string
  thumbnailUrl?: string
}

const FALLBACK_PRODUCTS: { product: ScannedProduct; weight: number }[] = [
  {
    weight: 0.7,
    product: {
      title: 'El Código del Asado Chileno',
      editionLabel: 'Pack Completo',
      priceNumber: 6390,
      priceText: '$6.390 CLP',
      thumbnailUrl:
        'https://cdn.magicpatterns.com/uploads/3uZftr2R2cX8Bfgs44MaCz/oferta_asado.jpg',
    },
  },
  {
    weight: 0.3,
    product: {
      title: 'El Código del Asado Chileno',
      editionLabel: 'Edición Digital',
      priceNumber: 6390,
      priceText: '$6.390 CLP',
      thumbnailUrl:
        'https://cdn.magicpatterns.com/uploads/3uZftr2R2cX8Bfgs44MaCz/oferta_asado.jpg',
    },
  },
]

// Parse a Chilean / generic price string into a number.
// Handles "$6.390", "6.390 CLP", "$27", "USD 27.00", "27", etc.
function parsePriceToNumber(raw: string): number | null {
  const cleaned = raw.replace(/[^\d.,]/g, '').trim()
  if (!cleaned) return null

  // Chilean format uses "." as thousands separator (e.g. 6.390 = 6390).
  // If there's a "." followed by exactly 3 digits and no comma, treat "." as thousands.
  let normalized = cleaned
  if (/^\d{1,3}(\.\d{3})+$/.test(cleaned)) {
    normalized = cleaned.replace(/\./g, '')
  } else if (cleaned.includes(',') && cleaned.includes('.')) {
    // e.g. 1.234,56 -> 1234.56
    normalized = cleaned.replace(/\./g, '').replace(',', '.')
  } else if (/,\d{2}$/.test(cleaned)) {
    // e.g. 27,00 -> 27.00
    normalized = cleaned.replace(',', '.')
  } else {
    normalized = cleaned.replace(/,/g, '')
  }

  const num = parseFloat(normalized)
  return Number.isFinite(num) ? num : null
}

function formatCLP(num: number): string {
  return `$${num.toLocaleString('es-CL')} CLP`
}

const PRICE_REGEX =
  /\$\s?\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{2})?|\b\d{1,3}(?:[.,]\d{3})+\b/
const CTA_KEYWORDS = [
  'add to cart',
  'buy',
  'checkout',
  'order',
  'comprar',
  'añadir al carrito',
  'quiero',
  'acceso inmediato',
]

function tryJsonLd(): ScannedProduct[] {
  const products: ScannedProduct[] = []
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[type="application/ld+json"]',
  )
  scripts.forEach((script) => {
    try {
      const data = JSON.parse(script.textContent || '')
      const nodes = Array.isArray(data) ? data : [data]
      nodes.forEach((node: any) => {
        if (node && node['@type'] === 'Product') {
          const offer = Array.isArray(node.offers)
            ? node.offers[0]
            : node.offers
          const priceNumber = offer
            ? parsePriceToNumber(String(offer.price ?? ''))
            : null
          if (priceNumber != null) {
            products.push({
              title: node.name || 'Producto',
              priceNumber,
              priceText: formatCLP(priceNumber),
              thumbnailUrl: Array.isArray(node.image)
                ? node.image[0]
                : node.image,
            })
          }
        }
      })
    } catch {
      /* ignore malformed JSON-LD */
    }
  })
  return products
}

function tryDomHeuristics(): ScannedProduct[] {
  const products: ScannedProduct[] = []

  // Find a CTA element to anchor on.
  const allElements = Array.from(
    document.querySelectorAll<HTMLElement>('button, a'),
  )
  const ctaEl = allElements.find((el) => {
    const text = (el.textContent || '').toLowerCase()
    return CTA_KEYWORDS.some((kw) => text.includes(kw))
  })

  const searchRoot =
    ctaEl?.closest('section') ||
    document.querySelector('#offer') ||
    document.body
  if (!searchRoot) return products

  // Price.
  const priceMatch = (searchRoot.textContent || '').match(PRICE_REGEX)
  const priceNumber = priceMatch ? parsePriceToNumber(priceMatch[0]) : null
  if (priceNumber == null) return products

  // Title — prefer a heading inside the section.
  const heading = searchRoot.querySelector('h1, h2, h3')
  const pageH1 = document.querySelector('h1')
  const title = (
    heading?.textContent ||
    pageH1?.textContent ||
    document.title ||
    'Producto'
  )
    .trim()
    .replace(/\s+/g, ' ')

  // Thumbnail — first meaningful image in the section.
  const img = searchRoot.querySelector<HTMLImageElement>('img')
  const thumbnailUrl = img?.src

  products.push({
    title,
    priceNumber,
    priceText: formatCLP(priceNumber),
    thumbnailUrl,
  })

  return products
}

/**
 * Scan the page for purchasable products. Returns a weighted catalog.
 * Falls back to the default Chilean product distribution if nothing is found.
 */
export function scanProducts(): { product: ScannedProduct; weight: number }[] {
  let found: ScannedProduct[] = []

  try {
    found = tryJsonLd()
    if (found.length === 0) found = tryDomHeuristics()
  } catch {
    found = []
  }

  if (found.length === 0) return FALLBACK_PRODUCTS

  const weight = 1 / found.length
  return found.map((product) => ({ product, weight }))
}

export function pickWeighted(
  catalog: { product: ScannedProduct; weight: number }[],
): ScannedProduct {
  const total = catalog.reduce((sum, c) => sum + c.weight, 0)
  let r = Math.random() * total
  for (const entry of catalog) {
    r -= entry.weight
    if (r <= 0) return entry.product
  }
  return catalog[0].product
}
